import { Calendar, Box, Grid, Text } from 'grommet';
import React, { useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useUsers } from '../../context/users';
import { Driver } from '../../context/model/types';
import { SubHeader } from '../../components';
import { createAppointment, deleteAppointment } from '../../components/TenantApp/createAppointment';
import { SLOT_TIMES, SlotKey, parseSchedule } from '../../utils/driverAvailability';

type DayAppointment = {
  id: string;
  driver: string;
  date: string;
  time: string;
  type: string;
  facilityID: string;
  orderID: string;
  tenantID: string;
  [key: string]: any;
};

type SlotStatus = {
  scheduled: boolean;
  available: boolean;
  paid: boolean;
  block?: DayAppointment;
  open?: DayAppointment;
};

function getSlotStatus(
  driver: Driver,
  dateKey: string,
  slotKey: SlotKey,
  dayAppointments: DayAppointment[],
): SlotStatus {
  const slotTime = SLOT_TIMES[slotKey];
  const matches = dayAppointments.filter(
    a => a.driver === driver.name && a.time === slotTime,
  );
  const block = matches.find(a => a.type === 'Block');
  const open = matches.find(a => a.type === 'Open');
  const bookings = matches.filter(a => a.type !== 'Block' && a.type !== 'Open').length;

  const schedule = parseSchedule(driver.availability);
  const dow = String(new Date(dateKey + 'T12:00:00').getDay());
  const dayConfig = schedule[dow];
  const slotCfg = dayConfig?.[slotKey];
  const scheduled = !!slotCfg?.enabled;

  const baseCapacity = scheduled ? (slotCfg!.capacity ?? 1) : 0;
  const effectiveCapacity = baseCapacity + (open ? 1 : 0);
  const available = !block && effectiveCapacity > 0 && bookings < effectiveCapacity;

  return {
    scheduled,
    available,
    paid: scheduled ? slotCfg!.paid : false,
    block,
    open,
  };
}

const SlotChip = ({
  label,
  priceLabel,
  available,
  dimmed,
  loading,
  onClick,
}: {
  label: string;
  priceLabel: string;
  available: boolean;
  dimmed: boolean;
  loading: boolean;
  onClick: () => void;
}) => {
  const bg = loading ? '#E5E7EB' : available ? '#D1FAE5' : '#F3F4F6';
  const textColor = loading ? '#9CA3AF' : available ? '#065F46' : '#9CA3AF';
  return (
    <span
      onClick={loading ? undefined : onClick}
      title={loading ? undefined : available ? `Block ${label}` : `Open ${label}`}
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 6,
        backgroundColor: bg,
        color: textColor,
        cursor: loading ? 'wait' : 'pointer',
        userSelect: 'none',
        lineHeight: 1.4,
        opacity: dimmed ? 0.6 : 1,
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 10 }}>{priceLabel}</span>
    </span>
  );
};

const CalendarPage = (): JSX.Element => {
  const [date, setDate] = useState<string | undefined>();
  const [loadingSlot, setLoadingSlot] = useState<string | null>(null);

  const { appointments2, setAppointments2, drivers, facilityInfo } = useUsers();

  const dayRefs = useRef<Record<string, any>>({});
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  const todayString = today.toLocaleDateString('en-CA');
  const endDate = new Date();
  endDate.setDate(today.getDate() + 60);
  const endDateString = endDate.toLocaleDateString('en-CA');

  const onSelect = (nextDate: string | string[]) => {
    const d = Array.isArray(nextDate) ? nextDate[0] : nextDate;
    setDate(d !== date ? d : undefined);
    const label = new Intl.DateTimeFormat('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    }).format(new Date(d));
    if (dayRefs.current[label] && gridContainerRef.current) {
      const container = gridContainerRef.current;
      container.scrollTo({
        top: dayRefs.current[label].offsetTop - container.offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const generateDays = () => {
    const days: Date[] = [];
    const cur = new Date(today);
    while (cur <= endDate) {
      days.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  };

  const mergedData = generateDays().map(day => {
    const dateKey = day.toLocaleDateString('en-CA');
    return {
      label: day.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      }),
      dateKey,
      appointments: ((appointments2 as any)[dateKey] || []) as DayAppointment[],
    };
  });

  const handleSlotClick = async (
    driver: Driver,
    dateKey: string,
    slotKey: SlotKey,
    dayAppointments: DayAppointment[],
  ) => {
    const slotTime = SLOT_TIMES[slotKey];
    const loadingKey = `${driver.id}-${dateKey}-${slotKey}`;
    setLoadingSlot(loadingKey);

    try {
      const status = getSlotStatus(driver, dateKey, slotKey, dayAppointments);

      // Click flips color: green→gray removes Open or adds Block; gray→green removes Block or adds Open.
      const toDelete = status.available ? status.open : status.block;

      if (toDelete) {
        await deleteAppointment(toDelete.id);
        setAppointments2((prev: any) => ({
          ...prev,
          [dateKey]: (prev[dateKey] || []).filter((a: DayAppointment) => a.id !== toDelete.id),
        }));
      } else {
        const newId = uuidv4();
        const recordType = status.available ? 'Block' : 'Open';
        await createAppointment({
          id: newId,
          driver: driver.name,
          date: dateKey,
          time: slotTime,
          type: recordType,
          facilityID: facilityInfo?.id ?? '',
          orderID: '',
          tenantID: '',
        });
        const newAppt: DayAppointment = {
          id: newId, driver: driver.name, date: dateKey, time: slotTime,
          type: recordType, facilityID: facilityInfo?.id ?? '', orderID: '', tenantID: '',
        };
        setAppointments2((prev: any) => ({
          ...prev,
          [dateKey]: [...(prev[dateKey] || []), newAppt],
        }));
      }
    } catch (err) {
      console.error('Slot toggle error:', err);
    } finally {
      setLoadingSlot(null);
    }
  };

  return (
    <>
      <SubHeader title="Calendar" />
      <Box direction="row" gap="xlarge" pad="small" justify="center">
        <Box pad="small">
          <Calendar
            date={date}
            daysOfWeek
            onSelect={onSelect}
            size="medium"
            bounds={[todayString, endDateString]}
          />
        </Box>

        <Box
          ref={gridContainerRef}
          overflow={{ vertical: 'auto' }}
          width="large"
          height={{ max: 'large' }}
          style={{ maxHeight: '70vh' }}
        >
          <Grid rows="auto" gap="medium">
            {mergedData.map(day => (
              <Box
                key={day.dateKey}
                ref={(el: any) => (dayRefs.current[day.label] = el)}
                pad="small"
                border={{ color: 'light-4', size: 'small' }}
                round="xsmall"
              >
                <Text size="small" weight="bold" margin={{ bottom: 'xsmall' }}>{day.label}</Text>

                {drivers.map(driver => (
                  <Box key={driver.id} direction="row" gap="small" margin={{ top: 'xsmall' }} align="center">
                    <Text size="xsmall" color="dark-3" style={{ minWidth: 80 }}>{driver.name}</Text>
                    {(['morning', 'afternoon'] as SlotKey[]).map(slotKey => {
                      const status = getSlotStatus(driver, day.dateKey, slotKey, day.appointments);
                      return (
                        <SlotChip
                          key={slotKey}
                          label={slotKey === 'morning' ? 'AM' : 'PM'}
                          priceLabel={!status.scheduled ? 'Off' : status.paid ? 'Paid' : 'Free'}
                          available={status.available}
                          dimmed={!status.scheduled}
                          loading={loadingSlot === `${driver.id}-${day.dateKey}-${slotKey}`}
                          onClick={() => handleSlotClick(driver, day.dateKey, slotKey, day.appointments)}
                        />
                      );
                    })}
                  </Box>
                ))}
              </Box>
            ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default CalendarPage;
